'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@/lib/utils';
import { Icons } from '@/components/ui/icons';

import { useRouter, usePathname } from 'next/navigation';
import { PiGlobeLight } from 'react-icons/pi';
import { GoChevronDown } from 'react-icons/go';

interface Language {
  name: string;
  slug: string;
  flag_url?: string;
}

interface LanguageSelectorProps {
  languages: Language[];
  currentLang: string;
  translations?: Record<string, string>; // Maps lang -> slug for single posts
  maxPages?: {
    en: number;
    ru: number;
  }; // Optional pagination limits for language switching
}

export default function LanguageSelector({
  languages,
  currentLang,
  maxPages,
  translations,
}: LanguageSelectorProps) {
  const router = useRouter();
  const pathname = usePathname();

  const handleLangChange = (newLang: string) => {
    // Determine the appropriate route based on current path and target language

    // If we're on a single post page
    if (pathname.includes('/posts/') && !pathname.endsWith('/posts') && !pathname.endsWith('/posts/ru')) {
      if (translations && translations[newLang]) {
        // Translation exists: navigate to translated post
        if (newLang === 'en') {
          router.push(`/posts/${translations[newLang]}`);
        } else {
          router.push(`/posts/${newLang}/${translations[newLang]}`);
        }
      } else {
        // No translation exists: redirect to main posts page
        if (newLang === 'en') {
          router.push('/posts');
        } else {
          router.push('/posts/ru');
        }
      }
      return;
    }

    // Helper function to check if a page number exists in target language
    const getMaxPageForLang = (lang: string) => {
      if (!maxPages) return Infinity; // If no limits provided, allow all pages
      return lang === 'en' ? maxPages.en : maxPages.ru;
    };

    // For all other pages (posts index, categories, pagination)
    if (pathname.startsWith('/posts/ru')) {
      // Currently on Russian route
      if (newLang === 'en') {
        // Switch to English: remove /ru from path
        const englishPath = pathname.replace('/posts/ru', '/posts');

        // Check if it's a pagination page
        const pageMatch = pathname.match(/\/posts\/ru\/page\/(\d+)/);
        if (pageMatch) {
          const pageNumber = parseInt(pageMatch[1]);
          const maxEnglishPages = getMaxPageForLang('en');

          // If the page doesn't exist in English, go to main posts page
          if (pageNumber > maxEnglishPages) {
            router.push('/posts');
            return;
          }
        }

        router.push(englishPath === '/posts/' ? '/posts' : englishPath);
      } else if (newLang === 'ru') {
        // Already on Russian, no change needed
        return;
      }
    } else if (pathname.startsWith('/posts')) {
      // Currently on English route
      if (newLang === 'ru') {
        // Switch to Russian: add /ru to path
        const russianPath = pathname.replace('/posts', '/posts/ru');

        // Check if it's a pagination page
        const pageMatch = pathname.match(/\/posts\/page\/(\d+)/);
        if (pageMatch) {
          const pageNumber = parseInt(pageMatch[1]);
          const maxRussianPages = getMaxPageForLang('ru');

          // If the page doesn't exist in Russian, go to main Russian posts page
          if (pageNumber > maxRussianPages) {
            router.push('/posts/ru');
            return;
          }
        }

        router.push(russianPath);
      } else if (newLang === 'en') {
        // Already on English, no change needed
        return;
      }
    }
  };

  return (
    <div className='flex gap-2 items-center rounded-lg'>
      <SelectPrimitive.Root value={currentLang} onValueChange={handleLangChange}>
        <SelectPrimitive.Trigger
          className={cn(
            'flex h-9 w-32 items-center rounded-[6px] justify-between whitespace-nowrap border border-border2 px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1'
          )}
        >
          <PiGlobeLight size={20} className='text-black' />
          <SelectPrimitive.Value>
            <div className='flex items-center gap-2'>
              {languages.find((l) => l.slug === currentLang)?.name}
            </div>
          </SelectPrimitive.Value>
          <SelectPrimitive.Icon asChild>
            <GoChevronDown size={20} className='text-zinc-400' />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className={cn(
              'relative z-50 max-h-72 bg-white min-w-[8rem] overflow-hidden rounded-md border text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
            )}
            position="popper"
          >
            <SelectPrimitive.Viewport className={cn('p-1')}>
              {languages.map((lang) => (
                <SelectPrimitive.Item
                  key={lang.slug}
                  value={lang.slug}
                  className={cn(
                    'relative flex w-full gap-2 cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                  )}
                >
                  <span className='flex h-3.5 w-3.5 items-center justify-center'>
                    <SelectPrimitive.ItemIndicator>
                      <Icons.Checkmark className='h-4 w-4 stroke-black' />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                  <SelectPrimitive.ItemText>
                    <div className='flex items-center gap-2'>{lang.name}</div>
                  </SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
}